import { useCallback } from 'react';
import { getSupabase, isSupabaseConfigured } from '../client';
import { useCachedFetch } from './useCachedFetch';
import type { CapaEstilo } from './useCapas';

/**
 * useCapasRegistry — registro de TODAS las capas del tenant (operativas e
 * informativas) con su conteo de geometrías, para el gestor del panel equipo.
 *
 * Lee la tabla `capas` (RLS SELECT tenant-scoped) con embeds PostgREST que
 * cuentan los hijos por FK:
 *   - parcelas_privadas(count) → predios de una capa operativa
 *   - capa_features(count)     → features de una capa informativa
 */

export interface CapaRegistroRow {
  id: string;
  nombre: string;
  tipo: 'operativa' | 'informativa';
  subtipo: string | null;
  estilo: CapaEstilo | null;
  visible: boolean;
  archivo_origen: string | null;
  formato: string | null;
  created_by_email: string | null;
  created_at: string;
  /** Predios (operativa) o features (informativa) según el tipo. */
  count: number;
}

interface RawCapaRow {
  id: string;
  nombre: string;
  tipo: 'operativa' | 'informativa';
  subtipo: string | null;
  estilo: CapaEstilo | null;
  visible: boolean;
  archivo_origen: string | null;
  formato: string | null;
  created_by_email: string | null;
  created_at: string;
  parcelas_privadas: { count: number }[] | null;
  capa_features: { count: number }[] | null;
}

export function useCapasRegistry(): {
  data: CapaRegistroRow[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
} {
  const fetcher = useCallback(async (): Promise<CapaRegistroRow[]> => {
    if (!isSupabaseConfigured()) return [];
    const sb = getSupabase();
    const { data, error } = await sb
      .from('capas')
      .select(
        'id, nombre, tipo, subtipo, estilo, visible, archivo_origen, formato, created_by_email, created_at, parcelas_privadas(count), capa_features(count)',
      )
      .order('created_at', { ascending: false });
    if (error) throw error;
    return ((data ?? []) as RawCapaRow[]).map((r) => ({
      id: r.id,
      nombre: r.nombre,
      tipo: r.tipo,
      subtipo: r.subtipo,
      estilo: r.estilo,
      visible: r.visible,
      archivo_origen: r.archivo_origen,
      formato: r.formato,
      created_by_email: r.created_by_email,
      created_at: r.created_at,
      count: r.tipo === 'operativa'
        ? (r.parcelas_privadas?.[0]?.count ?? 0)
        : (r.capa_features?.[0]?.count ?? 0),
    }));
  }, []);

  const { data, loading, error, refetch } = useCachedFetch<CapaRegistroRow[]>(
    'capas_registry',
    fetcher,
    { ttlMs: 20_000 },
  );

  return { data: data ?? [], loading, error, refetch };
}
